//  Modern Login Sayfası
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import ThemeToggle from '../../components/common/ThemeToggle/ThemeToggle';
import apiConfig from '../../config/config';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Mount animasyonu
  useEffect(() => {
    setMounted(true);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (process.env.NODE_ENV !== 'production') console.log('Login attempt for:', formData.username);
      
      // Login URL: Öncelik REACT_APP_TOKEN_URL, yoksa varsayılan token API
      const directTokenUrl = (process.env.REACT_APP_TOKEN_URL || 'http://78.186.223.18:8762/api/token').trim();
      const loginUrl = directTokenUrl || apiConfig.auth.login;

      const fetchFn = (/^https?:\/\//.test(loginUrl)) ? window.fetch.bind(window) : apiConfig.fetch.bind(apiConfig);

      const response = await fetchFn(loginUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: formData.username, password: formData.password })
      });

      // Response'u JSON'a çevir
      const data = await response.json();

      if (process.env.NODE_ENV !== 'production') {
        console.log('Full response object:', response);
        console.log('Response status:', response?.status);
        console.log('Response data:', data);
      }

      // Response kontrolü - yeni API formatına göre
      const isValidResponse = response.ok && (data?.data || data?.token || data?.access_token);
      const token = data?.data?.token || data?.token || data?.access_token || (typeof data === 'string' ? data : null);
      const refreshToken = data?.data?.refreshToken || data?.refreshToken || data?.refresh_token || null;
      const expiresAt = data?.data?.expiresAt || data?.expiresAt || null;

      if (process.env.NODE_ENV !== 'production') console.log('Response validation:', {
        hasToken: !!token,
        isValidResponse,
        expiresAt,
        statusCode: response.status,
        responseKeys: Object.keys(data || {})
      });

      // API response validasyonu
      if (!isValidResponse) {
        console.error('Invalid response format or status:', data);
        throw new Error(data?.message || 'Giriş başarısız: Geçersiz yanıt formatı');
      }

      if (!token) {
        console.error('Token not found in response:', data);
        throw new Error('Authentication token alınamadı');
      }

      // Token formatı: Sadece string olması yeterli (JWT şart değil)
      if (typeof token !== 'string' || token.length === 0) {
        console.error('Invalid token format:', token);
        throw new Error('Geçersiz authentication token formatı');
      }

      // User bilgilerini oluştur
      const user = {
        username: formData.username,
        token,
        refreshToken: refreshToken || null,
        expiresAt
      };

      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ Login successful - All validations passed');
        console.log('Token length:', token.length);
        console.log('Token expires at:', new Date(expiresAt));
        console.log('User info:', user);
      }

      // Token ve user bilgilerini kaydet
      localStorage.setItem('tegim-token', token);
      if (refreshToken) localStorage.setItem('tegim-refresh-token', refreshToken);
      localStorage.setItem('tegim-user', JSON.stringify(user));
      if (expiresAt) localStorage.setItem('tegim-expires-at', String(expiresAt));

      if (process.env.NODE_ENV !== 'production') console.log('Redirecting to dashboard...');
      navigate('/dashboard');

    } catch (err) {
      console.error('Login error details:', {
        error: err,
        message: err?.message,
        status: err?.status
      });
      
      // Hata durumunda localStorage'ı temizle
      localStorage.removeItem('tegim-token');
      localStorage.removeItem('tegim-user');
      localStorage.removeItem('tegim-expires-at');
      
      // Hata mesajını parse et
      let message = 'Bilinmeyen bir hata oluştu';
      
      if (err?.message) {
        message = err.message;
      } else if (err?.name === 'TypeError') {
        message = 'Sunucuya bağlanılamıyor. Arka uç çalıştığından emin olun.';
      }

      // HTTP status koduna göre özel mesajlar - fetch response'undan
      if (err instanceof TypeError && err.message.includes('fetch')) {
        message = 'Sunucuya bağlanılamıyor. Arka uç çalıştığından emin olun.';
      }
      
      setError(message);
      
      // Error shake animasyonu
      const form = document.querySelector('.login-form');
      if (form) {
        form.classList.add('shake');
        setTimeout(() => form.classList.remove('shake'), 600);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-primary flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative">

      {/*  Theme Toggle */}
      <div className={`absolute top-6 right-6 z-50 transition-all duration-500 ${
        mounted ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
      }`}>
        <ThemeToggle />
      </div>

      {/*  Background Logo */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <img
          src="/logo1.svg"
          alt=""
          className="w-[600px] h-[600px] opacity-5 select-none"
        />
      </div>

      {/*  Main Content */}
      <div className={`max-w-md w-full space-y-8 relative z-10 transition-all duration-700 ${
        mounted ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}>

        {/*  Header */}
        <div className="text-center">
          <div className={`mx-auto w-24 h-24 flex items-center justify-center transition-all duration-500 ${
            mounted ? 'scale-100' : 'scale-0'
          }`}>
            <img
              src="/logo1.svg"
              alt="Tegim Logosu"
              className="w-full h-full drop-shadow-lg"
            />
          </div>

          <h1 className={`mt-6 text-3xl font-bold text-primary transition-all duration-500 delay-200 ${
            mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}>
            Tegim'e Hoş Geldiniz
          </h1>

          <p className={`mt-2 text-secondary transition-all duration-500 delay-300 ${
            mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
          }`}>
            API Yönetim Platformuna giriş yapın
          </p>
        </div>

        {/*  Form Container */}
        <div className={`login-form card p-8 transition-all duration-500 delay-400 ${
          mounted ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'
        }`}>
          <form className="space-y-6" onSubmit={handleSubmit}>
            
            {/*  Username Field */}
            <div className="space-y-2">
              <label htmlFor="username" className="block text-sm font-medium text-secondary">
                Kullanıcı Adı
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-accent" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="input pl-10"
                  placeholder="Kullanıcı adınızı girin"
                />
              </div>
            </div>

            {/*  Password Field */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-secondary">
                Şifre
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-accent" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="input pl-10 pr-12"
                  placeholder="Şifrenizi girin"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-tertiary hover:text-accent transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/*  Error Message */}
            {error && (
              <div className="bg-error-light border border-error rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
                  <span className="text-sm text-error">{error}</span>
                </div>
              </div>
            )}

            {/*  Submit Button */}
            <div>
              <button
                type="submit"
                disabled={loading || !formData.username || !formData.password}
                className="btn btn-primary w-full flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    <span>Giriş yapılıyor...</span>
                  </>
                ) : (
                  <>
                    <LogIn className="w-5 h-5" />
                    <span>Giriş Yap</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/*  Footer */}
        <div className={`text-center transition-all duration-500 delay-600 ${
          mounted ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
        }`}>
          <p className="text-xs text-tertiary">
            © 2025 Tegim. Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
